import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, bulkDeleteGroupTemplates } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { GroupTemplate } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteGroupTemplateDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: GroupTemplate[];
  changeLogRoles: string[];
}

export default function DeleteGroupTemplateDialog({
  open,
  handleClose,
  selectedRows,
  changeLogRoles,
}: DeleteGroupTemplateDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const moreThanOneSelection = selectedRows.length > 1;

  const { mutate, isPending } = useMutation({
    mutationFn: () => bulkDeleteGroupTemplates(selectedRows, changeLogMessage),

    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted(
          `Group Template${moreThanOneSelection ? 's' : ''}`
        ),
      });

      queryClient.setQueryData<GroupTemplate[]>(
        [UserKeys.GroupTemplates],
        (old) => {
          if (old) {
            return old.filter(
              (template) =>
                !selectedRows.some(
                  (selected) => selected.keyString === template.keyString
                )
            );
          }
          return [];
        }
      );

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting(
          `group template${moreThanOneSelection ? 's' : ''}`
        ),
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.group_templates.delete_group_template, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groupTemplates,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.DeleteGroupTemplates.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={
        moreThanOneSelection
          ? 'Delete Groups Templates'
          : 'Delete Group Template'
      }
      dialogContent={
        <Box>
          <Typography>
            {moreThanOneSelection ? (
              <>
                Delete <strong>{selectedRows.length}</strong> group templates?
              </>
            ) : (
              <>
                Delete <strong>{selectedRows[0]?.groupTemplateName}</strong>?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: moreThanOneSelection ? 'Delete Templates' : 'Delete Template',
        color: 'error',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
